import { useTranslation } from 'react-i18next';
import { formatWithThousandSeparator } from '@/utils';

interface AdvanceCreditHeroProps {
  available: number;
  totalLoan: number;
  maxCashout: number;
}

/** One dark hero column. Money is `<bdi>` LTR-isolated + tabular-nums. */
function HeroCell({
  label,
  value,
  caption,
  big,
  bordered,
}: {
  label: string;
  value: number;
  caption: string;
  big?: boolean;
  bordered?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      className='px-7 py-6'
      style={bordered ? { borderInlineStart: '1px solid #2C2C28' } : undefined}
    >
      <div className='text-[12.5px] font-semibold uppercase tracking-[0.3px] text-[#B8B8AE]'>
        {label}
      </div>
      <div
        className={`mt-2 font-extrabold ${big ? 'text-[38px] tracking-[-1px]' : 'text-[24px] tracking-[-0.5px]'}`}
        style={{ fontVariantNumeric: 'tabular-nums' }}
      >
        <bdi dir='ltr'>
          {formatWithThousandSeparator(value)} {String(t('iqd'))}
        </bdi>
      </div>
      <div className='mt-1.5 text-[12.5px] text-[#9A9A90]'>{caption}</div>
    </div>
  );
}

/**
 * Credit hero (DESIGN State A §3) — dark card #151512, 3 columns
 * (1.6fr / 1fr / 1fr), uppercase labels. Stacks on narrow screens.
 */
function AdvanceCreditHero({ available, totalLoan, maxCashout }: AdvanceCreditHeroProps) {
  const { t } = useTranslation();
  return (
    <div
      className='mb-4 grid grid-cols-1 overflow-hidden rounded-[18px] text-white md:grid-cols-[1.6fr_1fr_1fr]'
      style={{ background: '#151512' }}
      data-testid='advance-cod-credit-hero'
    >
      <HeroCell
        label={String(t('advance_cod_remaining_balance'))}
        value={available}
        caption={String(t('advance_cod_remaining_balance_caption'))}
        big
      />
      <HeroCell
        label={String(t('advance_cod_total_loan'))}
        value={totalLoan}
        caption={String(t('advance_cod_total_loan_caption'))}
        bordered
      />
      <HeroCell
        label={String(t('advance_cod_max_payout'))}
        value={maxCashout}
        caption={String(t('advance_cod_max_payout_caption'))}
        bordered
      />
    </div>
  );
}

export default AdvanceCreditHero;
